import { LitElement, classMap, css, html, nothing } from "@lit";
import { checkIsTouchDevice, addToCart } from "@nova/utils";

class NovaProductCardQuickShop extends LitElement {
  static properties = {
    productOptions: {
      type: Array,
      attribute: "product-options",
      converter: (value) => value.split(","),
    },
    variants: { type: Array },
    activeVariant: {
      type: Number,
      attribute: "active-variant",
    },
    swatches: {
      type: Array,
    },
    swatchesData: {
      type: Array,
    },
    activeId: {
      type: String,
      attribute: "active-id",
    },
    url: {
      type: String,
      attribute: "url",
    },
    activeUrl: {
      type: String,
      attribute: "activeUrl",
    },
    allVariants: {
      type: Array,
    },
    showAddButton: {
      state: true,
    },
    showNotifyMeButton: {
      state: true,
    },
    loading: {
      type: Boolean,
      attribute: "loading",
    },
    callOut: {
      type: String,
    },
    inventoryQty: {
      type: String,
    },
    variantUrls: {
      type: Object
    },
    preOrderLabel: {
      type: String,
    },
  };

  get isTouchDevice() {
    return checkIsTouchDevice();
  }

  get activeSwatchVariants() {
    if (this.selectedSwatch) {
      return (this.allVariants || []).filter((variant) =>
        variant.options?.includes(this.selectedSwatch),
      )
    }

    if (this.variants.length) {
      return this.variants
    }

    return this.allVariants || []
  }

  get optionsToShow() {
    const variants = this.activeSwatchVariants

    if (!variants.length) {
      return this.productOptions
    }

    return this.productOptions.filter((option) =>
      variants.find((variant) => variant.options.includes(option)),
    )
  }

  get isSingleVariantSwatch() {
    return this.activeSwatchVariants.length === 1
  }

  constructor() {
    super();
    this.showAddButton = false;
    this.showNotifyMeButton = false;
    this.loading = false;
    this.addedToCart = false;
    this.variantUrls = {};
    this.preOrderLabel = '';
    this.selectedSwatch = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("swatch-selected", this.handleSwatchSelected);

    setTimeout(() => {
      if (this.isSingleVariantSwatch) {
        this.showAddButton = true
        this.activeVariant = this.activeSwatchVariants[0]
      }
    }, 500)
  }

  findVariantByOption(option) {
    const source = this.activeSwatchVariants;
    const [singleVariant] = source;

    if (source.length === 1) {
      return singleVariant;
    }

    return source.find(variant => variant.options?.includes(option));
  }

  resolveSingleOptionVariant(variantId) {
    const variants = this.activeSwatchVariants
    const parsedVariantId = variantId ? Number.parseInt(variantId, 10) : null

    if (parsedVariantId) {
      const matchedVariant = variants.find((variant) => variant.id === parsedVariantId)
      if (matchedVariant) return matchedVariant
    }

    return variants[0]
  }

  setActiveVariant(variant) {
    this.activeVariant = variant;
  }

  handleOptionClick(event) {
    const { option, variantId } = event.target.dataset;

    this.activeOption = option;
    const activeVariant = this.activeSwatchVariants.find(
      variant => variant.id === Number.parseInt(variantId, 10)
    );

    this.setActiveVariant(activeVariant);
    this.addedToCart = false;

    if (activeVariant.available) {
      this.showAddButton = true;
      this.showNotifyMeButton = false;
    } else {
      this.showAddButton = false;
      this.showNotifyMeButton = true;
    }

    this.dispatchEvent(
      new CustomEvent("option-selected", {
        bubbles: true,
        composed: true,
        detail: {
          variant: activeVariant,
        },
      })
    );
  }

  async handleAddClick() {
    this.loading = true;

    try {
      const properties = this.parseCallOutProperties(this.callOut);

      await addToCart({
        id: this.activeVariant?.id,
        quantity: 1,
        properties,
      });

      this.addedToCart = true;
    } catch (error) {
      console.error('Add to cart failed:', error);
    } finally {
      this.loading = false;
    }
  }

  parseCallOutProperties(callOut) {
    const properties = {};

    if (callOut && typeof callOut === 'string') {
      const pairs = callOut
        .split(',')
        .map(pair => pair.split(':').map(part => part.trim()))
        .filter(([key, value]) => key && value);

      for (const [key, value] of pairs) {
        properties[key] = value;
      }
    }

    if (this.preOrderLabel) {
      properties._pre_order = this.preOrderLabel;
    }

    return properties;
  }

  handleNotifyMeClick() {
    console.log("notify me");
  }

  handleSwatchSelected(event) {
    this.showAddButton = false
    this.showNotifyMeButton = false
    this.selectedSwatch = event.detail?.swatch || null

    if (!this.isSingleVariantSwatch) {
      this.activeVariant = null
      this.activeOption = null
      this.addedToCart = false
      return
    }

    const activeVariant = this.resolveSingleOptionVariant(event.detail?.variantId)
    if (!activeVariant) {
      this.activeVariant = null
      this.activeOption = null
      this.addedToCart = false
      return
    }

    this.activeVariant = activeVariant
    this.activeOption = this.optionsToShow[0] || null
    this.addedToCart = false

    if (activeVariant.available) {
      this.showAddButton = true
      this.showNotifyMeButton = false
    } else {
      this.showAddButton = false
      this.showNotifyMeButton = true
    }
  }

  render() {
    if (this.isTouchDevice) return nothing;
    const containerClasses = {
      "quick-shop-container": true,
      "quick-shop-container--one-row": this.optionsToShow.length < 2,
    };
    return html`
      <div class="${classMap(containerClasses)}quick-shop-container" part="quick-shop-container">
        <nova-product-card-swatches
          .swatches=${this.swatches}
          .swatchesData=${this.swatchesData}
          .variants=${this.allVariants}
          .activeId=${this.activeId}
          .url=${this.url}
          .variantUrls=${this.variantUrls}
          is-quick-shop="true"
        >
        </nova-product-card-swatches>
        <div class="quick-shop__options-container">
          <div class="quick-shop__option_selector">
            ${this.optionsToShow.map((option, i) => {
              const variant = this.findVariantByOption(option);
              if (!variant) return nothing;
              const stateClasses = {
                "quick-shop__option": true,
                "quick-shop__option--sold-out": !variant?.available,
                "quick-shop__option--selected":
                  variant?.id === this.activeVariant?.id,
                "hidden": this.isSingleVariantSwatch,
              };

              return html`
                <sl-button
                  class="${classMap(stateClasses)}"
                  data-variant-id=${variant && variant.id}
                  data-option=${option}
                  aria-label="option: ${option}"
                  variant="text"
                  size="small"
                  @click=${this.handleOptionClick}
                >
                  ${this.isSingleVariantSwatch ? "One Size" : option}
                </sl-button>
              `;
            })}
          </div>

          <div class="quick-shop__button-container">
            ${this.showAddButton
              ? html`<sl-button
                  variant="text"
                  size="none"
                  class="quick-shop__button"
                  ?loading=${this.loading}
                  @click=${this.handleAddClick}
                  >${this.addedToCart ? "Added" : "Add"}</sl-button
                >`
              : nothing}
            ${this.showNotifyMeButton
              ? html`<sl-button
                  variant="text"
                  size="none"
                  class="quick-shop__button"
                  href="${this.activeUrl}#notify-me"
                  >Notify Me</sl-button
                >`
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  static styles = css`
    .quick-shop-container {
      width: 100%;
      padding: 16px;
      background-color: var(--nova-color-black);
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .quick-shop-container--one-row {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }

    .quick-shop-container--one-row nova-product-card-swatches {
      display: flex;
    }

    .quick-shop__option_selector {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .quick-shop__option {
      display: flex;
      align-items: center;
    }

    .quick-shop__option::part(base) {
      border-color: transparent;
      width: fit-content;
      min-height: auto;
      padding: 0;
      border-radius: 0;
    }

    .quick-shop__option--sold-out::part(base) {
      cursor: pointer;
      opacity: 0.3;
    }

    .quick-shop__option::part(base):hover {
      color: var(--nova-brand-ink);
      border-bottom: 1px solid black;
    }

    .quick-shop__option--sold-out::part(label) {
      text-decoration: line-through;
    }

    .quick-shop__option::part(label) {
      padding: 0;
      font: var(--nova-body-font);
      color: var(--nova-color-white);
      font-size: 14px;
      line-height: 1.35;
    }

    .quick-shop__option--selected::part(base) {
      border-bottom: 1px solid var(--nova-color-white) !important;
    }

    .quick-shop__option--selected::part(label) {
      color: var(--nova-color-white);
    }

    .quick-shop__option--sold-out + .quick-shop__option--sold-out {
      margin-left: 5px;
    }

    .quick-shop__option--sold-out {
      text-decoration: line-through;
      color: var(--nova-brand-subdued);
    }

    .quick-shop__options-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .quick-shop__button::part(label) {
      color: var(--nova-color-white);
      font: var(--nova-body-font);
      font-size: 14px;
      line-height: 1.35;
    }

    @media (max-width: 767px) {
      .quick-shop__button-container {
        display: none;
      }
    }

    .hidden {
      display: none;
    }
  `;
}

customElements.define("nova-product-card-quick-shop", NovaProductCardQuickShop);
